import type { WidgetContainerProps } from '../components/WidgetContainer';
import type { ProductAssistantWidgetHandle } from './mount';
import { createLauncherHelper, type LauncherController, type LauncherOffsets, defaultLauncherOffsets } from './launcherHelper';
import { ensureI18nLocaleSafe, i18nInstance } from '../localization/i18n';

type WidgetRuntime = {
    mount: (target: HTMLElement, props?: WidgetContainerProps) => ProductAssistantWidgetHandle;
    open?: () => void;
    close?: () => void;
    dock?: () => void;
    undock?: () => void;
    send?: (message: string) => boolean;
};

type LoaderConfig = {
    targetSelector?: string;
    widgetSource?: string;
    language?: string;
    endpoint?: string;
    authTokenProvider?: string;
    onEnrichContext?: string;
    hasLegacyAuthorization: boolean;
    authenticationKey?: string;
    sourceName?: string;
    countryCode?: string;
    isVisible?: boolean;
    defaultDocked?: boolean;
    onDockChanged?: string;
    onClose?: string;
    enableLauncher?: boolean;
    launcherLabel?: string;
    floatingBottomOffset?: number;
    floatingRightOffset?: number;
    showHeader?: boolean;
    hideHeader?: boolean;
    showDuration?: boolean;
    hideDuration?: boolean;
    dockingEnabled?: boolean;
    exampleQuestionsCacheTtlMinutes?: number;
};

declare global {
    interface Window {
        SKFProductAssistantWidget?: WidgetRuntime;
    }
}

type RuntimeCache = Record<string, Promise<WidgetRuntime>>;

const runtimeCache: RuntimeCache = {};
const DEFAULT_CHAT_ENDPOINT = 'https://product-assist.skf.com/public/assistant/chat';
const defaultFloatingOffsets: LauncherOffsets = {
    bottom: defaultLauncherOffsets.bottom,
    right: defaultLauncherOffsets.right
};

bootstrap();

function bootstrap(): void {
    const script = document.currentScript as HTMLScriptElement | null;

    if (!script) {
        console.error('[SKF Widget] Unable to locate the current script element.');
        return;
    }

    whenDocumentReady(() => mountFromScript(script));
}

async function mountFromScript(script: HTMLScriptElement): Promise<void> {
    const config = readConfig(script);

    if (config.hasLegacyAuthorization) {
        console.error('[SKF Widget] data-authorization is no longer supported. Use data-auth-token-provider instead.');
        return;
    }

    const target = await resolveTarget(script, config.targetSelector);

    if (!target) {
        console.error('[SKF Widget] Provide a target <div> via data-target or wrap the loader script in the desired host element.');
        return;
    }

    if (!(target instanceof HTMLDivElement)) {
        console.error('[SKF Widget] The host element must be a <div>.');
        return;
    }

    if (!config.authenticationKey) {
        console.error('[SKF Widget] data-authentication-key is required and cannot be empty.');
        return;
    }

    const widgetSrc = resolveWidgetSource(script, config.widgetSource);

    if (!widgetSrc) {
        console.error('[SKF Widget] Unable to infer the widget bundle URL. Set data-widget-src on the loader script.');
        return;
    }

    const runtime = await loadRuntime(widgetSrc, script.nonce).catch((error) => {
        console.error('[SKF Widget] Failed to load widget runtime.', error);
        return null;
    });

    if (!runtime) {
        return;
    }

    await ensureI18nLocaleSafe(config.language);
    const resolvedOffsets = resolveFloatingOffsets(config);
    const launcher =
        config.enableLauncher === false
            ? undefined
            : createLauncherHelper({
                  label: config.launcherLabel ?? i18nInstance.t('launchWidget'),
                  bottom: resolvedOffsets.bottom,
                  right: resolvedOffsets.right
              });

    let props: WidgetContainerProps;
    try {
        props = buildWidgetProps(config);
    } catch (error) {
        console.error('[SKF Widget] Failed to build widget configuration.', error);
        return;
    }

    prepareTarget(target, script);
    const floatingOffsets = props.floatingOffsets ?? resolvedOffsets;
    props.floatingOffsets = floatingOffsets;
    const visibilityController = createVisibilityController(props, launcher, config.isVisible);
    const dockStateTracker = createDockStateTracker(props.defaultDocked ?? false);
    const userDockChanged = props.onDockChanged;
    props.onDockChanged = (isDocked: boolean) => {
        dockStateTracker.setCurrentDocked(isDocked);
        userDockChanged?.(isDocked);
    };
    const userClose = props.onClose;
    props.onClose = () => {
        dockStateTracker.rememberDockStateForReopen();
        visibilityController.hideWidget();
        userClose?.();
    };

    launcher?.setOnClick(() => visibilityController.showWidget());

    const handle = runtime.mount(target, props);
    visibilityController.attachHandle(handle);
    registerControlActions(
        runtime,
        createWidgetControlActions(handle, visibilityController, dockStateTracker, userClose)
    );
}

function readConfig(script: HTMLScriptElement): LoaderConfig {
    const legacyAuthorization = readAttribute(script, 'authorization', 'auth', 'auth-token');

    return {
        targetSelector: sanitizeValue(readAttribute(script, 'target', 'data-target', 'root')),
        widgetSource: sanitizeValue(readAttribute(script, 'widget-src', 'widgetsource')),
        language: sanitizeValue(readAttribute(script, 'language', 'lang')),
        endpoint: sanitizeValue(readAttribute(script, 'endpoint', 'chat-endpoint')) ?? DEFAULT_CHAT_ENDPOINT,
        authTokenProvider: sanitizeValue(readAttribute(script, 'auth-token-provider', 'authTokenProvider')),
        onEnrichContext: sanitizeValue(readAttribute(script, 'on-enrich-context', 'onEnrichContext')),
        hasLegacyAuthorization: sanitizeValue(legacyAuthorization) !== undefined,
        authenticationKey: sanitizeValue(readAttribute(script, 'authentication-key', 'auth-key')),
        sourceName: sanitizeValue(readAttribute(script, 'source-name', 'source')),
        countryCode: sanitizeValue(readAttribute(script, 'country-code', 'countryCode')),
        isVisible: parseBoolean(readAttribute(script, 'visible', 'is-visible', 'isVisible')),
        defaultDocked: parseBoolean(readAttribute(script, 'docked', 'default-docked', 'defaultDocked')),
        onDockChanged: sanitizeValue(readAttribute(script, 'on-dock-changed', 'onDockChanged')),
        onClose: sanitizeValue(readAttribute(script, 'on-close', 'onClose')),
        enableLauncher: parseBoolean(readAttribute(script, 'enable-launcher', 'launcher')),
        launcherLabel: sanitizeValue(readAttribute(script, 'launcher-label')),
        floatingBottomOffset: parseNumber(readAttribute(script, 'floating-bottom', 'undocked-bottom')),
        floatingRightOffset: parseNumber(readAttribute(script, 'floating-right', 'undocked-right')),
        showHeader: parseBoolean(readAttribute(script, 'show-header', 'header-visible')),
        hideHeader: parseBoolean(readAttribute(script, 'hide-header', 'header-hidden', 'no-header')),
        showDuration: parseBoolean(readAttribute(script, 'show-duration', 'duration-visible')),
        hideDuration: parseBoolean(readAttribute(script, 'hide-duration', 'duration-hidden')),
        dockingEnabled: parseBoolean(readAttribute(script, 'docking-enabled', 'dock-enabled', 'allow-docking')),
        exampleQuestionsCacheTtlMinutes: parseNumber(
            readAttribute(script, 'example-questions-cache-ttl-minutes', 'exampleQuestionsCacheTtlMinutes')
        )
    };
}

async function resolveTarget(script: HTMLScriptElement, selector?: string | null): Promise<HTMLElement | null> {
    if (selector) {
        const element = document.querySelector(selector);
        if (!element) {
            console.error(`[SKF Widget] Could not find target element for selector "${selector}".`);
        }

        return element as HTMLElement | null;
    }

    const parent = script.parentElement;
    if (parent) {
        return parent;
    }

    return null;
}

function resolveWidgetSource(script: HTMLScriptElement, override?: string | null): string | null {
    const trimmed = sanitizeValue(override);

    if (trimmed) {
        return new URL(trimmed, window.location.href).toString();
    }

    if (script.src) {
        return new URL('./widget.js', script.src).toString();
    }

    return null;
}

function loadRuntime(src: string, nonce?: string | null): Promise<WidgetRuntime> {
    if (window.SKFProductAssistantWidget) {
        return Promise.resolve(window.SKFProductAssistantWidget);
    }

    if (!runtimeCache[src]) {
        runtimeCache[src] = new Promise<WidgetRuntime>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = src;
            script.async = true;
            if (nonce) {
                script.nonce = nonce;
            }

            script.onload = () => {
                if (window.SKFProductAssistantWidget) {
                    resolve(window.SKFProductAssistantWidget);
                    return;
                }

                reject(new Error('Widget runtime loaded without registering the global API.'));
            };

            script.onerror = (event) => {
                reject(new Error(`Failed to load widget runtime from ${src}.`));
                if (event instanceof ErrorEvent) {
                    console.error('[SKF Widget] Runtime error event:', event.message);
                }
            };

            document.head.appendChild(script);
        }).catch((error) => {
            delete runtimeCache[src];
            throw error;
        });
    }

    return runtimeCache[src];
}

function prepareTarget(target: HTMLDivElement, script: HTMLScriptElement): void {
    if (script.parentElement === target) {
        target.removeChild(script);
    }

    target.replaceChildren();
}

function buildWidgetProps(config: LoaderConfig): WidgetContainerProps {
    const props: WidgetContainerProps = {};

    if (config.language) {
        props.language = config.language;
    }

    if (config.isVisible !== undefined) {
        props.isVisible = config.isVisible;
    }

    if (config.defaultDocked !== undefined) {
        props.defaultDocked = config.defaultDocked;
    }

    const onDockChanged = resolveCallback<(isDocked: boolean) => void>(config.onDockChanged);
    if (onDockChanged) {
        props.onDockChanged = onDockChanged;
    }

    const onClose = resolveCallback<() => void>(config.onClose);
    if (onClose) {
        props.onClose = onClose;
    }

    if (config.authenticationKey) {
        const headers = normalizeHeaders({
            authenticationKey: config.authenticationKey,
            sourceName: config.sourceName,
            countryCode: config.countryCode
        });

        const getAuthorizationToken = config.authTokenProvider
            ? resolveTokenProvider(config.authTokenProvider)
            : undefined;
        const onEnrichContext = config.onEnrichContext
            ? resolveEnrichContextCallback(config.onEnrichContext)
            : undefined;
        if (config.authTokenProvider && !getAuthorizationToken) {
            throw new Error(`[SKF Widget] Token provider "${config.authTokenProvider}" was not found on window.`);
        }
        if (config.onEnrichContext && !onEnrichContext) {
            throw new Error(`[SKF Widget] Enrich context callback "${config.onEnrichContext}" was not found on window.`);
        }

        props.chat = {
            endpoint: config.endpoint ?? DEFAULT_CHAT_ENDPOINT,
            headers,
            getAuthorizationToken,
            onEnrichContext
        };
    }

    props.floatingOffsets = resolveFloatingOffsets(config);

    if (config.showHeader !== undefined) {
        props.showHeader = config.showHeader;
    } else if (config.hideHeader !== undefined) {
        props.showHeader = !config.hideHeader;
    }

    if (config.showDuration !== undefined) {
        props.showResponseDuration = config.showDuration;
    } else if (config.hideDuration !== undefined) {
        props.showResponseDuration = !config.hideDuration;
    }

    if (config.dockingEnabled !== undefined) {
        props.dockingEnabled = config.dockingEnabled;
    }

    if (config.exampleQuestionsCacheTtlMinutes !== undefined) {
        props.exampleQuestionsCacheTtlMinutes = config.exampleQuestionsCacheTtlMinutes;
    }

    return props;
}

function normalizeHeaders(options: {
    authenticationKey: string;
    sourceName?: string;
    countryCode?: string;
}): Record<string, string> {
    const headers: Record<string, string> = {};

    headers['Ocp-Apim-Subscription-Key'] = options.authenticationKey;

    if (options.sourceName) {
        headers['x-source'] = options.sourceName;
    }

    if (options.countryCode) {
        headers['x-country-code'] = options.countryCode;
    }

    return headers;
}

function resolveTokenProvider(reference?: string): (() => string | Promise<string>) | undefined {
    const callback = resolveWindowFunction(reference);
    if (!callback) {
        return undefined;
    }

    return () => {
        const result = callback.call(window);
        if (typeof result !== 'string' && !(result instanceof Promise)) {
            throw new Error('[SKF Widget] Token provider must return a string or Promise<string>.');
        }

        return result;
    };
}

function resolveEnrichContextCallback(reference?: string): (() => unknown | Promise<unknown>) | undefined {
    const callback = resolveWindowFunction(reference);
    if (!callback) {
        return undefined;
    }

    return () => {
        try {
            return callback.call(window);
        } catch (error) {
            console.warn(`[SKF Widget] Error while executing enrich context callback "${reference}".`, error);
            return undefined;
        }
    };
}

function readAttribute(script: HTMLScriptElement, ...names: string[]): string | null {
    for (const name of names) {
        if (!name) {
            continue;
        }

        const normalized = name.replace(/([A-Z])/g, '-$1').toLowerCase();
        const datasetKey = normalized.replace(/-([a-z0-9])/g, (_, group) => group.toUpperCase());

        const directValue =
            script.getAttribute(name) ??
            script.getAttribute(name.toLowerCase()) ??
            script.getAttribute(normalized) ??
            script.getAttribute(`data-${normalized}`);

        if (directValue !== null && directValue !== undefined) {
            return directValue;
        }

        const datasetValue = script.dataset?.[datasetKey as keyof DOMStringMap];

        if (datasetValue !== undefined) {
            return datasetValue;
        }
    }

    return null;
}

function sanitizeValue(value?: string | null): string | undefined {
    const trimmed = value?.trim();
    return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

function parseBoolean(value?: string | null): boolean | undefined {
    if (!value) {
        return undefined;
    }

    const normalized = value.trim().toLowerCase();

    if (!normalized) {
        return undefined;
    }

    if (['true', '1', 'yes', 'on'].includes(normalized)) {
        return true;
    }

    if (['false', '0', 'no', 'off'].includes(normalized)) {
        return false;
    }

    return undefined;
}

function resolveCallback<T extends (...args: any[]) => void>(reference?: string): T | undefined {
    const callback = resolveWindowFunction(reference);
    if (!callback) {
        return undefined;
    }

    return ((...args: Parameters<T>) => {
        try {
            callback.apply(window, args);
        } catch (error) {
            console.error(`[SKF Widget] Error while executing callback "${reference}".`, error);
        }
    }) as T;
}

function resolveWindowFunction(reference?: string): ((...args: unknown[]) => unknown) | undefined {
    if (!reference) {
        return undefined;
    }

    const path = reference.split('.').map((segment) => segment.trim()).filter(Boolean);

    if (!path.length) {
        return undefined;
    }

    let context: unknown = window;

    for (const key of path) {
        if (context && typeof context === 'object' && key in (context as Record<string, unknown>)) {
            context = (context as Record<string, unknown>)[key];
        } else {
            console.error(`[SKF Widget] Callback "${reference}" was not found on window.`);
            return undefined;
        }
    }

    if (typeof context !== 'function') {
        console.error(`[SKF Widget] Callback "${reference}" is not a function.`);
        return undefined;
    }

    return context as (...args: unknown[]) => unknown;
}

function parseNumber(value?: string | null): number | undefined {
    if (!value) {
        return undefined;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}

function whenDocumentReady(callback: () => void): void {
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
        callback();
        return;
    }

    document.addEventListener('DOMContentLoaded', () => callback(), { once: true });
}

type VisibilityController = {
    attachHandle: (handle: ProductAssistantWidgetHandle) => void;
    showWidget: () => void;
    hideWidget: () => void;
};

type WidgetControlActions = {
    open: () => void;
    close: () => void;
    dock: () => void;
    undock: () => void;
    send: (message: string) => boolean;
};

type DockStateTracker = {
    setCurrentDocked: (isDocked: boolean) => void;
    rememberDockStateForReopen: () => void;
    getReopenDockedState: () => boolean;
};

function createVisibilityController(
    props: WidgetContainerProps,
    launcher?: LauncherController,
    explicitVisibility?: boolean
): VisibilityController {
    let handle: ProductAssistantWidgetHandle | null = null;
    let isVisible = props.isVisible ?? true;

    if (launcher) {
        if (explicitVisibility === undefined) {
            isVisible = false;
            props.isVisible = false;
        } else if (isVisible) {
            launcher.hideButton();
        } else {
            launcher.showButton();
        }
    }

    const updateVisibility = (next: boolean) => {
        if (isVisible === next) {
            return;
        }

        isVisible = next;

        if (handle) {
            handle.update({ isVisible: next });
        } else {
            props.isVisible = next;
        }

        if (launcher) {
            if (next) {
                launcher.hideButton();
            } else {
                launcher.showButton();
            }
        }
    };

    return {
        attachHandle(nextHandle) {
            handle = nextHandle;
            handle.update({ isVisible });
        },
        showWidget() {
            updateVisibility(true);
        },
        hideWidget() {
            updateVisibility(false);
        }
    };
}

function createWidgetControlActions(
    handle: ProductAssistantWidgetHandle,
    visibilityController: VisibilityController,
    dockStateTracker: DockStateTracker,
    onClose?: () => void
): WidgetControlActions {
    return {
        open() {
            const reopenDockedState = dockStateTracker.getReopenDockedState();
            dockStateTracker.setCurrentDocked(reopenDockedState);
            handle.update({ defaultDocked: reopenDockedState });
            visibilityController.showWidget();
        },
        close() {
            dockStateTracker.rememberDockStateForReopen();
            dockStateTracker.setCurrentDocked(false);
            handle.update({ defaultDocked: false });
            visibilityController.hideWidget();
            onClose?.();
        },
        dock() {
            dockStateTracker.setCurrentDocked(true);
            handle.update({ defaultDocked: true });
        },
        undock() {
            dockStateTracker.setCurrentDocked(false);
            handle.update({ defaultDocked: false });
        },
        send(message: string): boolean {
            return handle.send(message);
        }
    };
}

function createDockStateTracker(initialDocked: boolean): DockStateTracker {
    let currentDocked = initialDocked;
    let reopenDocked = initialDocked;

    return {
        setCurrentDocked(isDocked: boolean) {
            currentDocked = isDocked;
        },
        rememberDockStateForReopen() {
            reopenDocked = currentDocked;
        },
        getReopenDockedState() {
            return reopenDocked;
        }
    };
}

function registerControlActions(runtime: WidgetRuntime, actions: WidgetControlActions): void {
    runtime.open = actions.open;
    runtime.close = actions.close;
    runtime.dock = actions.dock;
    runtime.undock = actions.undock;
    runtime.send = actions.send;
}

function resolveFloatingOffsets(config: LoaderConfig): LauncherOffsets {
    const bottom = Number.isFinite(config.floatingBottomOffset ?? NaN)
        ? (config.floatingBottomOffset as number)
        : defaultFloatingOffsets.bottom;
    const right = Number.isFinite(config.floatingRightOffset ?? NaN)
        ? (config.floatingRightOffset as number)
        : defaultFloatingOffsets.right;

    return {
        bottom,
        right
    };
}
